import type {LoggerDomain, LogLevel} from "@cm/log"
import {LOGGER_DOMAINS} from "@cm/log"
import {loggerFactory} from "#browser-log/factory"

/**
 * Console utilities exposed on window.cmLog for runtime debugging.
 */
export interface CmLogConsoleApi {
    /**
     * Set the global log level.
     * @example cmLog.setLevel('debug')
     */
    setLevel: (level: LogLevel) => void

    /**
     * Set the log level for a specific domain.
     * @example cmLog.setDomainLevel('api', 'debug')
     */
    setDomainLevel: (domain: LoggerDomain, level: LogLevel) => void

    /**
     * Clear the domain-specific level (revert to global).
     * @example cmLog.clearDomainLevel('api')
     */
    clearDomainLevel: (domain: LoggerDomain) => void

    /**
     * Get current configuration.
     * @example cmLog.getConfig()
     */
    getConfig: () => {global: LogLevel; domains: Partial<Record<LoggerDomain, LogLevel>>}

    /**
     * Reset all configuration to defaults.
     * @example cmLog.reset()
     */
    reset: () => void

    /**
     * List all available domains.
     * @example cmLog.domains
     */
    domains: readonly LoggerDomain[]

    /**
     * Show help.
     * @example cmLog.help()
     */
    help: () => void
}

declare global {
    interface Window {
        cmLog: CmLogConsoleApi
    }
}

/**
 * Install the cmLog console API on the window object.
 * Call this once during application bootstrap.
 */
export function installConsoleApi(): void {
    if (typeof window === "undefined") {
        return
    }

    window.cmLog = {
        setLevel: (level: LogLevel) => {
            loggerFactory.setGlobalLevel(level)
            console.log(`[cmLog] Global log level set to: ${level}`) // eslint-disable-line no-console
        },

        setDomainLevel: (domain: LoggerDomain, level: LogLevel) => {
            loggerFactory.setDomainLevel(domain, level)
            console.log(`[cmLog] Log level for '${domain}' set to: ${level}`) // eslint-disable-line no-console
        },

        clearDomainLevel: (domain: LoggerDomain) => {
            loggerFactory.clearDomainLevel(domain)
            console.log(`[cmLog] Log level for '${domain}' cleared (using global)`) // eslint-disable-line no-console
        },

        getConfig: () => {
            const config = loggerFactory.getConfig()
            console.log("[cmLog] Current configuration:", config) // eslint-disable-line no-console
            return config
        },

        reset: () => {
            loggerFactory.reset()
            console.log("[cmLog] Configuration reset to defaults") // eslint-disable-line no-console
        },

        domains: LOGGER_DOMAINS,

        help: () => {
            // eslint-disable-next-line no-console
            console.log(`
[cmLog] Console API Help
========================

Commands:
  cmLog.setLevel(level)              - Set global log level
  cmLog.setDomainLevel(domain, level) - Set domain-specific level
  cmLog.clearDomainLevel(domain)     - Clear domain override
  cmLog.getConfig()                  - Show current configuration
  cmLog.reset()                      - Reset to defaults
  cmLog.domains                      - List available domains
  cmLog.help()                       - Show this help

Log levels (in order of verbosity):
  'error' - Only errors (default)
  'warn'  - Errors and warnings
  'info'  - Errors, warnings, and info
  'log'   - All except debug and trace
  'debug' - All except trace
  'trace' - Everything (most verbose)

Available domains:
  ${LOGGER_DOMAINS.join(", ")}

Examples:
  cmLog.setLevel('debug')           - Enable all logging
  cmLog.setDomainLevel('api', 'debug') - Debug only API calls
  cmLog.setDomainLevel('routing', 'info') - Info+ for routing
`)
        },
    }
}
